/**
 * Settings → Company profile route.
 *
 * Loads `companies.get` (cached via TanStack Query), hydrates the form,
 * and wires the SaveBar to the diff payload + `companies.update`.
 *
 * Save model: client-side dirty tracking (form values vs hydrated
 * baseline). The SaveBar shows "All changes saved · X ago" when clean,
 * "Unsaved changes" when dirty, "Saving…" during the mutation, and
 * "Saved" briefly after success. Discard reverts to baseline.
 *
 * Locked-field handling: PAN / CIN / GSTIN are stripped from the payload
 * client-side when their baseline is non-empty (the server enforces it
 * as the floor). If the server still returns CONFLICT (race / wrong
 * client check), the field-level error renders on the right field.
 *
 * @see prototype/settings/settings-company.html — visual reference
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { Alert } from "@flint/ui";
import { hasPermission } from "@flint/api-contract";
import { trpc } from "../../../../lib/trpc.ts";
import { formatRelativeTime } from "../../../../lib/format.ts";
import { isTrpcCauseShape } from "../../../../lib/trpc-errors.ts";
import { meQueryOptions } from "../../../../lib/session.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { SettingsShell, SaveBar, type SaveBarState } from "../../../../features/settings/index.ts";
import { usePermission } from "../../../../lib/permissions.ts";
import {
  CompanyForm,
  formFromCompany,
  valuesToUpdatePayload,
  isDirtyVs,
  EMPTY_COMPANY_FORM,
  type CompanyFormValues,
} from "../../../../features/settings/company/index.ts";

export const Route = createFileRoute("/(workspace)/settings/company/")({
  // URL-level gate: render the app's 404 page for non-`settings.manage`
  // roles who try to open a Settings URL directly (topbar Settings link is
  // already hidden for them). We use `notFound()` rather than a silent
  // redirect so the block is visible; `defaultNotFoundComponent` in
  // `apps/web/src/main.tsx:24` renders the shared NotFound screen. This
  // is UX defense-in-depth on top of the server-side `permissionGate` on
  // the mutation procedures.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "settings.manage")) {
      throw notFound();
    }
  },
  component: CompanyProfilePage,
});

function CompanyProfilePage() {
  const utils = trpc.useUtils();
  const company = trpc.companies.get.useQuery({});
  // Server-side gate is `permissionGate("settings.manage")` on the router;
  // this hook is UX softening only (form becomes view-only, SaveBar hides).
  const canEdit = usePermission("settings.manage");

  // Two pieces of state:
  //   - `baseline` = the snapshot we diff against (last saved server state)
  //   - `values`   = the live form values
  // Hydrating both from `company.data` keeps "saved" === "baseline match".
  const [baseline, setBaseline] = useState<CompanyFormValues>(EMPTY_COMPANY_FORM);
  const [values, setValues] = useState<CompanyFormValues>(EMPTY_COMPANY_FORM);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof CompanyFormValues, string>>>(
    {},
  );

  // Hydrate when the wire data arrives or refetches.
  useEffect(() => {
    if (!company.data) return;
    const next = formFromCompany(company.data);
    setBaseline(next);
    // Only reset live values when they ARE the baseline (avoid clobbering
    // the user's in-flight edits on a background refetch).
    setValues((current) => (isDirtyVs(current, baseline) ? current : next));
    // We deliberately depend on company.data only — the baseline check
    // uses the previous baseline via setValues' updater closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.data]);

  const update = trpc.companies.update.useMutation({
    onSuccess: async () => {
      // Refetch company to pick up any server-side normalisation (trim,
      // case-folding) and reset baseline to the server's view of truth.
      await utils.companies.get.invalidate();
      setSavedAt(new Date());
      setFieldErrors({});
    },
  });

  const dirty = isDirtyVs(values, baseline);

  // Page-level alert copy. `update.error.message` for a Zod validation
  // failure is the raw stringified `zodIssues` array — surface the first
  // issue's clean message instead. Locked-field CONFLICT is already
  // handled per-field via `fieldErrors`, so hide the alert when any
  // field-level error is set.
  const alertServerError = useMemo(() => {
    if (!update.error) return null;
    if (Object.keys(fieldErrors).length > 0) return null;
    const first = firstZodIssueMessage(update.error);
    return first ?? update.error.message;
  }, [update.error, fieldErrors]);

  const saveBarState: SaveBarState = update.isPending
    ? "saving"
    : dirty
      ? "dirty"
      : savedAt
        ? "saved"
        : "idle";

  const statusText = useMemo(() => {
    if (update.isPending) return "Saving…";
    if (dirty) return "Unsaved changes — review before saving";
    if (savedAt) return `All changes saved · ${formatRelativeTime(savedAt)}`;
    return "No changes yet";
  }, [update.isPending, dirty, savedAt]);

  const onSave = useCallback(async () => {
    const payload = valuesToUpdatePayload(values, baseline);
    if (!payload) return; // No-op safety: the SaveBar disables in this state too.

    setFieldErrors({});
    try {
      await update.mutateAsync(payload);
    } catch (err: unknown) {
      // Map locked-field CONFLICT into a field-level error. The cause
      // shape is `{ reason: 'locked-field', field: 'pan' | 'cin' | 'gstin' }`
      // — narrowed by the trpc-errors helper rather than a raw `as` cast.
      if (isTrpcCauseShape(err, { reason: "locked-field" })) {
        const field = err.data.cause.field;
        if (field === "pan" || field === "cin" || field === "gstin") {
          setFieldErrors({
            [field]: `${field.toUpperCase()} is locked once set. Contact support to change it.`,
          });
        }
        return;
      }
      // Zod validation → map each issue to its top-level form key so the
      // relevant `<Field>` renders an inline red message. Falls through
      // to `alertServerError` if no issue maps to a known key.
      const zodIssues = extractZodIssues(err);
      if (zodIssues) {
        const map: Partial<Record<keyof CompanyFormValues, string>> = {};
        for (const issue of zodIssues) {
          const key = typeof issue.path?.[0] === "string" ? issue.path[0] : null;
          if (key && !(key in map)) {
            map[key as keyof CompanyFormValues] = friendlyZodMessage(issue);
          }
        }
        if (Object.keys(map).length > 0) setFieldErrors(map);
      }
      // Anything else renders via `alertServerError` in the form's
      // `serverError` slot — no special handling here.
    }
  }, [values, baseline, update]);

  const onDiscard = useCallback(() => {
    setValues(baseline);
    setFieldErrors({});
  }, [baseline]);

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Settings" }, { label: "Company profile" }]}>
      <SettingsShell
        activeSection="company"
        pageTitle="Company profile"
        pageDescription="How Flint identifies your business on invoices, bills, and tax filings. Visible to customers, vendors, and the GST portal."
      >
        {company.isLoading ? (
          <p className="text-muted text-sm">Loading company profile…</p>
        ) : company.error ? (
          <Alert
            intent="bad"
            title="Couldn't load company profile"
            description={company.error.message}
          />
        ) : company.data ? (
          <>
            <CompanyForm
              values={values}
              baseline={baseline}
              logoUrl={company.data.logoUrl}
              fieldErrors={fieldErrors}
              serverError={alertServerError}
              readOnly={!canEdit}
              onChange={setValues}
            />
            {canEdit ? (
              <SaveBar
                state={saveBarState}
                statusText={statusText}
                onDiscard={onDiscard}
                onSave={() => void onSave()}
              />
            ) : null}
          </>
        ) : null}
      </SettingsShell>
    </WorkspaceShell>
  );
}

interface ZodIssueLike {
  code?: string;
  message?: string;
  path?: readonly (string | number)[];
}

/**
 * Extract the Zod issue array off a caught tRPC error, or `null` when the
 * error didn't carry one. The server's `errorFormatter`
 * (`apps/api/src/trpc.ts`) attaches Zod issues as `error.data.zodIssues`.
 */
function extractZodIssues(err: unknown): readonly ZodIssueLike[] | null {
  if (typeof err !== "object" || err === null) return null;
  const data = (err as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return null;
  const issues = (data as { zodIssues?: unknown }).zodIssues;
  if (!Array.isArray(issues) || issues.length === 0) return null;
  return issues as readonly ZodIssueLike[];
}

/**
 * Convert a Zod issue into a short, user-facing message. Zod's default
 * "String must contain at least 1 character(s)" reads awkward; swap for
 * "Required." Everything else passes through.
 */
function friendlyZodMessage(issue: ZodIssueLike): string {
  const msg = issue.message ?? "Invalid value.";
  if (issue.code === "too_small" && /at least 1 character/i.test(msg)) return "Required.";
  return msg;
}

/**
 * Pull the first Zod issue's message off a caught tRPC error, or `null`
 * when the error didn't carry one. Used as the page-level alert fallback
 * when no issue maps to a known top-level form key.
 */
function firstZodIssueMessage(err: unknown): string | null {
  const issues = extractZodIssues(err);
  if (!issues) return null;
  return friendlyZodMessage(issues[0]!);
}
